// Client-side network store — Zustand with belt-and-suspenders persistence.
//
// TWO persistence mechanisms run in parallel because each covers a gap
// the other can't:
//
//   1. `NETWORK_PREFERENCE` cookie  — written + read by `setUserNetwork`
//      and `hydrateFromCookie`. The cookie is what the SERVER reads via
//      `getServerNetwork()` for SSR-aware fetches; localStorage is
//      invisible to the server, so this layer is non-negotiable.
//
//   2. Zustand `persist` middleware → localStorage (`morfi-network`).
//      Survives scenarios where the cookie is unavailable: dev server
//      port changes (cookies are scoped per host:port — `localhost:3000`
//      cookies are invisible on `localhost:3001`), private-mode rules
//      around third-party cookie clearing, browser extensions that nuke
//      cookies, or any case where the cookie is dropped but localStorage
//      survives. localStorage is keyed by ORIGIN HOST, so a port change
//      still sees the stored value, and the cookie gets re-written from
//      localStorage on first set.
//
// Components consume this via `useUserNetwork()` to read the current
// network and `setUserNetwork()` to flip. Both layers are written on
// every flip so they stay in lockstep.
//
// SDK / RPC re-init on toggle is the responsibility of the consumers
// of this store, not the store itself. This module is pure state.

'use client';

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getCookie, setCookie } from '@/lib/config/cookies';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

import { getDefaultNetwork } from './config';
import {
  NETWORK_PREFERENCE_COOKIE,
  NETWORK_PREFERENCE_MAX_AGE,
  parseNetworkPreference,
} from './preference';
import { isNetwork, type Network } from './types';

export interface NetworkState {
  /** Current network. Initialised to deployment default; hydrated from
   *  the cookie + localStorage by `useUserNetwork()` on mount. */
  current: Network;
  /** Set the user's network. Persists to BOTH the cookie (for SSR) and
   *  localStorage (for client durability across port/cookie loss). */
  setUserNetwork: (next: Network) => void;
  /** Hydrate the store from the cookie. Called once on mount by the
   *  network-sync provider so SSR / hydration stay in sync. localStorage
   *  is hydrated automatically by the persist middleware — this method
   *  only handles the cookie path. */
  hydrateFromCookie: () => void;
}

interface PersistedNetworkShape {
  current: Network;
}

/** The persisted `current`, or null when it is absent, malformed, or a
 *  network this deployment does not configure. */
// ALLOW-UNKNOWN: the raw localStorage payload has no type until it is narrowed here.
function readPersistedNetwork(persisted: unknown): Network | null {
  if (typeof persisted !== 'object' || persisted === null || !('current' in persisted)) {
    return null;
  }
  return parseNetworkPreference(typeof persisted.current === 'string' ? persisted.current : null);
}

export const useNetworkStore: UseBoundStore<StoreApi<NetworkState>> =
  createTrackedStore<NetworkState>('useNetworkStore')(
    persist(
      (set): NetworkState => ({
        current: getDefaultNetwork(),
        setUserNetwork: (next: Network): void => {
          // Mirror to the cookie (SSR reads it via getServerNetwork).
          // The persist middleware mirrors to localStorage automatically
          // when the store mutates below.
          setCookie(NETWORK_PREFERENCE_COOKIE, next, NETWORK_PREFERENCE_MAX_AGE);
          set({ current: next });
        },
        hydrateFromCookie: (): void => {
          // Cookie wins over localStorage when both exist — the cookie
          // is what the server already used for the in-flight request,
          // and disagreeing with the server would cause an SDK
          // re-bind on first paint. When the cookie is MISSING but
          // localStorage has a value, we copy it back into the cookie
          // so the next SSR request agrees with the client.
          const raw = getCookie(NETWORK_PREFERENCE_COOKIE);
          const parsed = parseNetworkPreference(raw);
          if (parsed !== null) {
            set({ current: parsed });
            return;
          }
          // Cookie missing — recover from whatever the persist middleware
          // hydrated (already in `state.current` by now). Refresh the
          // cookie so the server sees it on the next request.
          const fromStore = useNetworkStore.getState().current;
          if (isNetwork(fromStore)) {
            setCookie(NETWORK_PREFERENCE_COOKIE, fromStore, NETWORK_PREFERENCE_MAX_AGE);
          }
        },
      }),
      {
        name: 'morfi-network',
        storage: createJSONStorage((): Storage => localStorage),
        // Only persist `current` — the action functions are recreated
        // on store init and never need to be serialized.
        partialize: (state: NetworkState): PersistedNetworkShape => ({
          current: state.current,
        }),
        // A persisted network is only honoured when this deployment
        // configures it. Builds before #2429 wrote `local` into every
        // visitor's localStorage on devnet-configured deployments, and
        // `hydrateFromCookie` copies whatever was rehydrated back into the
        // cookie when the cookie is missing — so a stale `local` survived
        // every later build and tagged production sessions as `local`.
        // ALLOW-UNKNOWN: zustand's `merge` signature types the persisted state this way.
        merge: (persisted: unknown, current: NetworkState): NetworkState => ({
          ...current,
          current: readPersistedNetwork(persisted) ?? current.current,
        }),
      },
    ),
  );
